import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import { Plus, PencilSimple, Trash, Plant, CurrencyCircleDollar, Basket, Handshake, ChartBar, Ruler } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EMPTY_CROP, HARVEST_UNIT_OPTIONS, today, fmt } from "./constants";


export function SalesTab({ crops, sales, reload }) {
  const [form, setForm] = useState({
    crop_id: "", date: today(), amount: "", unit: "ton",
    unit_price: "", buyer: "", notes: "",
  });
  const [filterCrop, setFilterCrop] = useState("");

  const selectedCrop = crops.find(c => c.id === form.crop_id);
  const remaining = selectedCrop ? Number(selectedCrop.remaining_amount || 0) : null;

  const onCropChange = (cid) => {
    const c = crops.find(x => x.id === cid);
    setForm(f => ({
      ...f,
      crop_id: cid,
      unit: c?.last_unit || "ton",
      unit_price: c?.last_unit_price || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.crop_id) return toast.error("Ürün seçin");
    if (!(Number(form.amount) > 0)) return toast.error("Miktar 0'dan büyük olmalı");
    if (!(Number(form.unit_price) >= 0)) return toast.error("Birim fiyat geçersiz");
    if (!form.buyer.trim()) return toast.error("Alıcı (müşteri/tüccar) giriniz");
    try {
      const r = await api.post("/fields/sales", {
        crop_id: form.crop_id,
        date: form.date,
        amount: Number(form.amount),
        unit: form.unit,
        unit_price: Number(form.unit_price),
        buyer: form.buyer.trim(),
        notes: form.notes || null,
      });
      toast.success(`Satış kaydedildi: ${fmt(r.data.total_value)} ₺ · Finans'a gelir olarak eklendi ✓`);
      setForm({ ...form, amount: "", unit_price: "", buyer: "", notes: "" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    }
  };

  const del = async (s) => {
    if (!window.confirm(`"${s.crop_name}" satışı silinsin mi?\nAlıcı: ${s.buyer}\nTutar: ${fmt(s.total_value)} ₺\n\nBu işlem Finans'taki gelir kaydını da siler.`)) return;
    try {
      await api.delete(`/fields/sales/${s.id}`);
      toast.success("Satış (ve bağlı finans kaydı) silindi");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const cropName = (cid) => crops.find(c => c.id === cid)?.name ?? "—";
  const filtered = filterCrop ? sales.filter(s => s.crop_id === filterCrop) : sales;
  const filteredVal = useMemo(() => filtered.reduce((s, x) => s + Number(x.total_value || 0), 0), [filtered]);

  const previewTotal = Number(form.amount || 0) * Number(form.unit_price || 0);
  const overSelling = remaining !== null && Number(form.amount) > remaining;

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="fields-sales-tab">
      <Card>
        <h3 className="heading text-lg mb-3">Mahsul Satışı Ekle</h3>
        {crops.length === 0 ? (
          <div className="text-sm text-[#8a938c]">Önce Genel sekmesinden bir ürün ekleyin.</div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field label="Ürün *">
              <Select required data-testid="fields-sales-crop" value={form.crop_id} onChange={e => onCropChange(e.target.value)}>
                <option value="">Seçin...</option>
                {crops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            {selectedCrop && (
              <div className="text-xs bg-[#F5F2EC] border border-[#E5E2DC] px-3 py-2 rounded" data-testid="fields-sales-remaining">
                <div className="flex justify-between">
                  <span className="text-[#5e675e]">Toplam Hasat</span>
                  <b className="tabnum">{fmt(selectedCrop.total_harvest_amount)} {selectedCrop.last_unit || "birim"}</b>
                </div>
                <div className="flex justify-between mt-0.5">
                  <span className="text-[#5e675e]">Şimdiye Kadar Satılan</span>
                  <b className="tabnum text-[#C45B45]">{fmt(selectedCrop.total_sold_amount || 0)} {selectedCrop.last_unit || "birim"}</b>
                </div>
                <div className="flex justify-between mt-0.5 pt-1 border-t border-dashed border-[#E5E2DC]">
                  <span className="text-[#3E5B36] font-medium">Satışa Uygun (kalan)</span>
                  <b className="tabnum text-[#3E5B36]">{fmt(remaining)} {selectedCrop.last_unit || "birim"}</b>
                </div>
              </div>
            )}
            <Field label="Tarih *">
              <Input type="date" required data-testid="fields-sales-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Miktar *">
                <Input type="number" step="0.01" min="0.01" required data-testid="fields-sales-amount"
                       value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
              </Field>
              <Field label="Birim *">
                <Select data-testid="fields-sales-unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                  {HARVEST_UNIT_OPTIONS.map(u => <option key={u}>{u}</option>)}
                </Select>
              </Field>
              <Field label={`Birim Fiyat (₺/${form.unit}) *`}>
                <Input type="number" step="0.01" min="0" required data-testid="fields-sales-price"
                       value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} />
              </Field>
            </div>
            <Field label="Alıcı (Müşteri / Tüccar) *">
              <Input required data-testid="fields-sales-buyer" placeholder="Örn: Ahmet Bey, XYZ Yem Ltd."
                     value={form.buyer} onChange={e => setForm({ ...form, buyer: e.target.value })} />
            </Field>
            {previewTotal > 0 && (
              <div className={`text-sm px-3 py-2 rounded-md ${overSelling ? "bg-[#FBECEA] border border-[#E9B5AB] text-[#8B2E1D]" : "bg-[#E4EEE6] border border-[#c2d6c8] text-[#2D4A36]"}`}
                   data-testid="fields-sales-preview">
                {overSelling ? "⚠️" : "✓"} {form.amount} {form.unit} × {form.unit_price} ₺ = <b>{fmt(previewTotal)} ₺</b> gelir
                {overSelling && <div className="text-[11px] mt-1">Kalan hasattan fazla satmak istiyorsunuz. Kayıt yine de tutulur, ama stokta karşılığı olmayabilir.</div>}
              </div>
            )}
            <Field label="Notlar">
              <Input data-testid="fields-sales-notes" value={form.notes} placeholder="Ödeme koşulu, teslim tarihi vs."
                     onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <div className="text-xs text-[#5e675e] bg-[#F5F2EC] border border-[#E5E2DC] px-3 py-2 rounded">
              💰 Bu satış otomatik olarak <b>Finans → Gelirler</b> tablosuna "mahsul satışı" kategorisinde eklenir.<br/>
              <span className="text-[#8a938c]">Silindiğinde finans kaydı da otomatik silinir.</span>
            </div>
            <Button type="submit" data-testid="fields-sales-submit"><Plus size={14} className="inline mr-1" /> Satışı Kaydet</Button>
          </form>
        )}
      </Card>

      <Card className="lg:col-span-2 !p-0 overflow-hidden">
        <div className="p-4 pb-3 border-b border-[#E5E2DC] flex flex-wrap items-center gap-2 justify-between">
          <h3 className="heading text-lg">Satış Geçmişi</h3>
          <div className="flex items-center gap-3">
            <Select data-testid="fields-sales-filter" value={filterCrop} onChange={e => setFilterCrop(e.target.value)} className="!py-1 text-xs">
              <option value="">Tümü</option>
              {crops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <span className="text-xs text-[#8a938c]">
              Toplam Gelir: <b className="text-[#3E5B36]">{fmt(filteredVal)} ₺</b>
            </span>
          </div>
        </div>
        <table className="data-table">
          <thead><tr><th>Tarih</th><th>Ürün</th><th>Alıcı</th><th className="text-right">Miktar</th><th className="text-right">Fiyat</th><th className="text-right">Tutar</th><th>Notlar</th><th></th></tr></thead>
          <tbody>
            {filtered.slice(0, 200).map(x => (
              <tr key={x.id} data-testid={`fields-sales-row-${x.id}`}>
                <td className="tabnum">{x.date}</td>
                <td>{cropName(x.crop_id)}</td>
                <td className="font-medium">{x.buyer}</td>
                <td className="text-right tabnum font-semibold">{fmt(x.amount)} {x.unit || "birim"}</td>
                <td className="text-right tabnum">{fmt(x.unit_price)} ₺</td>
                <td className="text-right tabnum font-semibold text-[#3E5B36]">{fmt(x.total_value)} ₺</td>
                <td className="text-xs text-[#6b746c]">{x.notes || "—"}</td>
                <td>
                  <button data-testid={`fields-sales-del-${x.id}`}
                          onClick={() => del(x)}
                          className="text-[#C45B45] hover:text-[#8B2E1D] p-1">
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={8} className="text-center py-8 text-[#8a938c]">Satış kaydı yok</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


// ---------------- ALAN BAZLI MALİYET ANALİZİ ----------------
